import { X } from 'lucide-react';
import type { FilterCondition, FilterConfig, FilterMode } from '../../types/filters';

interface ActiveFilterChipsProps {
  filters: FilterCondition[];
  filterConfigs: FilterConfig[];
  filterMode: FilterMode;
  onChange: (filters: FilterCondition[], mode: FilterMode) => void;
}

const OPERATOR_LABELS: Record<string, string> = {
  equals: '=',
  notEquals: '≠',
  notEqualsOrNull: '≠',
  contains: 'contient',
  notContains: 'ne contient pas',
  startsWith: 'commence par',
  notStartsWith: 'ne commence pas par',
  endsWith: 'finit par',
  notEndsWith: 'ne finit pas par',
  greaterThan: '>',
  lessThan: '<',
  before: 'avant',
  after: 'après',
  between: 'entre',
  sameDay: 'le',
  isNotNull: 'est défini',
  isNull: 'n’est pas défini',
};

function formatValue(filter: FilterCondition, config?: FilterConfig): string {
  if (filter.operator === 'isNull' || filter.operator === 'isNotNull') return '';
  const values = Array.isArray(filter.value) ? filter.value : [filter.value];
  return values
    .map((value) => config?.options?.find((option) => option.value === value)?.label || value)
    .join(' – ');
}

export function ActiveFilterChips({
  filters,
  filterConfigs,
  filterMode,
  onChange,
}: ActiveFilterChipsProps) {
  if (filters.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 px-6 py-2 bg-zinc-50 border-b border-zinc-200">
      <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">
        Filtres {filterMode === 'and' ? 'ET' : 'OU'}
      </span>
      {filters.map((filter, index) => {
        const config = filterConfigs.find((candidate) => candidate.field === filter.field);
        return (
          <span
            key={`${filter.field}-${index}`}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white border border-zinc-200 text-xs text-zinc-700 rounded-full shadow-xs"
          >
            <span className="font-medium">{config?.label || filter.label || filter.field}</span>
            <span>{OPERATOR_LABELS[filter.operator]}</span>
            <span>{formatValue(filter, config)}</span>
            <button
              type="button"
              onClick={() =>
                onChange(
                  filters.filter((_, itemIndex) => itemIndex !== index),
                  filterMode
                )
              }
              className="text-zinc-400 hover:text-zinc-900 transition-colors"
              aria-label={`Retirer le filtre ${config?.label || filter.field}`}
            >
              <X className="size-3" />
            </button>
          </span>
        );
      })}
      <button
        type="button"
        onClick={() => onChange([], 'and')}
        className="text-xs font-medium text-zinc-500 hover:text-zinc-900 transition-colors"
      >
        Tout effacer
      </button>
    </div>
  );
}
